import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { IBasket, IBasketItem } from "../models/IBasket";
import { ICustomerAddress } from "../models/ICustomerAddress";
import { DEFAULT_CURRENCY_ID, IPaymentMethod, IPreOrderBody } from "../models/IOrder";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { createOrder, createPreOrder, getPaymentMethods } from "../services/order";
import { getDefaultAddress, getUserToken } from "../utils/tokenUtils";
import { BASKET_EVENT } from "../utils/BasketContext";
import { useActiveOrder } from "../utils/ActiveOrderContext";
import { useLanguage } from "../context/LanguageContext";

const TWO_HOURS = 2 * 60 * 60 * 1000;

interface Props {
  onChangeAddress: () => void;
}

function SepetPopup({ onChangeAddress }: Props) {
  const { t, lang } = useLanguage();
  const { refreshActiveOrder } = useActiveOrder();
  const [items, setItems] = useState<IBasketItem[]>([]);
  const [minAmount, setMinAmount] = useState(0);
  const [defaultAddr, setDefaultAddr] = useState<ICustomerAddress | null>(null);
  const [deliveryTownIds, setDeliveryTownIds] = useState<string[]>([]);
  const [orderStatus, setOrderStatus] = useState<"idle" | "ordering" | "confirming" | "placing" | "success" | "error">("idle");
  const [orderError, setOrderError] = useState("");
  const [preOrderId, setPreOrderId] = useState<string | null>(null);
  const [preOrderTotalPrice, setPreOrderTotalPrice] = useState<number | null>(null);
  const [originalTotalPrice, setOriginalTotalPrice] = useState<number | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const [itemDiscountTotal, setItemDiscountTotal] = useState<number | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const changeAddressClickedRef = useRef(false);

  const loadBasket = () => {
    const stored = localStorage.getItem("basket");
    if (stored) {
      try {
        const basket: IBasket = JSON.parse(stored);
        if (basket.lastUpdated && Date.now() - basket.lastUpdated > TWO_HOURS) {
          localStorage.removeItem("basket");
          setItems([]);
          setMinAmount(0);
        } else {
          setItems(basket.items ?? []);
          setMinAmount(basket.minAmount ?? 0);
        }
      } catch {
        setItems([]);
      }
    } else {
      setItems([]);
      setMinAmount(0);
    }

    setDefaultAddr(getDefaultAddress());

    try {
      const ids = JSON.parse(localStorage.getItem("restaurantDeliveryTownIds") ?? "[]");
      setDeliveryTownIds(Array.isArray(ids) ? ids : []);
    } catch {
      setDeliveryTownIds([]);
    }

    setOrderStatus("idle");
    setPreOrderId(null);
    setPreOrderTotalPrice(null);
    setOriginalTotalPrice(null);
    setDiscountAmount(null);
    setItemDiscountTotal(null);
    setPaymentMethods([]);
    setSelectedPaymentId(null);
    setPromoCode("");
  };

  const removeItem = (index: number) => {
    const stored = localStorage.getItem("basket");
    if (!stored) return;
    try {
      const basket: IBasket = JSON.parse(stored);
      basket.items.splice(index, 1);
      localStorage.setItem("basket", JSON.stringify(basket));
      window.dispatchEvent(new CustomEvent(BASKET_EVENT));
      setItems([...basket.items]);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    loadBasket();
    const modalEl = document.getElementById("sepetModal");
    if (!modalEl) return;
    const handleHide = () => {
      if (changeAddressClickedRef.current) {
        changeAddressClickedRef.current = false;
        onChangeAddress();
      }
    };
    modalEl.addEventListener("show.bs.modal", loadBasket);
    modalEl.addEventListener("hidden.bs.modal", handleHide);
    return () => {
      modalEl.removeEventListener("show.bs.modal", loadBasket);
      modalEl.removeEventListener("hidden.bs.modal", handleHide);
    };
  }, []);

  // What a unit actually costs, campaign included — the same price the menu showed.
  const unitPriceOf = (item: IBasketItem) =>
    item.discountedPrice != null ? item.discountedPrice : item.foodPrice;

  const total = items.reduce((sum, item) => {
    const extraCost = item.menuUpgrade.reduce(
      (s, m) => s + m.menuUpgradePrice * m.menuUpgradeQuantity,
      0
    );
    return sum + (unitPriceOf(item) + extraCost) * item.foodQuantity;
  }, 0);

  // Pre-campaign total, for showing what per-dish campaigns saved before the server confirms it.
  const grossTotal = items.reduce((sum, item) => {
    const extraCost = item.menuUpgrade.reduce(
      (s, m) => s + m.menuUpgradePrice * m.menuUpgradeQuantity,
      0
    );
    return sum + (item.foodPrice + extraCost) * item.foodQuantity;
  }, 0);

  const localItemDiscount = grossTotal - total;

  const remaining = minAmount > 0 ? Math.max(0, minAmount - total) : 0;
  const canOrder = remaining === 0 && items.length > 0;
  const addressValid =
    !!defaultAddr &&
    (deliveryTownIds.length === 0 || deliveryTownIds.includes(defaultAddr.townId));
  const orderEnabled = canOrder && addressValid;

  const handlePlaceOrder = async () => {
    if (!orderEnabled) return;
    const token = getUserToken();
    if (!token || !defaultAddr) return;

    const stored = localStorage.getItem("basket");
    if (!stored) return;
    let basket: IBasket;
    try {
      basket = JSON.parse(stored);
    } catch {
      return;
    }

    const body: IPreOrderBody = {
      userId: token.userId,
      totalAmount: total,
      foodList: basket.items.map((item) => ({
        foodId: item.foodId,
        foodQuantity: item.foodQuantity,
        foodNote: item.foodNote,
        menuUpgrade: item.menuUpgrade,
      })),
      restourantId: basket.restourantId,
      currencyId: DEFAULT_CURRENCY_ID,
      ...(promoCode.trim() ? { promoCode: promoCode.trim() } : {}),
    };

    setOrderStatus("ordering");
    setOrderError("");
    try {
      const [preOrderRes, paymentRes] = await Promise.all([
        createPreOrder(body),
        getPaymentMethods(basket.restourantId),
      ]);
      setPreOrderId(preOrderRes.data.id);
      setPreOrderTotalPrice(preOrderRes.data.preOrderTotalPrice);
      setOriginalTotalPrice(preOrderRes.data.originalTotalPrice);
      setDiscountAmount(preOrderRes.data.discountAmount);
      setItemDiscountTotal(preOrderRes.data.itemDiscountTotal ?? null);
      const activeMethods = paymentRes.data.filter((m) => m.isActive);
      setPaymentMethods(activeMethods);
      setSelectedPaymentId(null);
      setOrderStatus("confirming");
    } catch (err: unknown) {
      let msg = t("cart.orderFailed");
      if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        if (typeof data === "string") msg = data;
        else if (Array.isArray(data) && data[0]?.description) msg = data[0].description;
        else if (data?.message) msg = data.message;
        else if (data?.title) msg = data.title;
        else msg = JSON.stringify(data ?? err.message);
      }
      setOrderError(msg);
      setOrderStatus("error");
    }
  };

  const handleConfirmOrder = async () => {
    if (!preOrderId || !defaultAddr) return;
    setOrderStatus("placing");
    setOrderError("");
    try {
      await createOrder({ preOrderId, orderNote: "", customerAddressId: defaultAddr.id, paymentMethodId: selectedPaymentId! });
      localStorage.removeItem("basket");
      window.dispatchEvent(new CustomEvent(BASKET_EVENT));
      setItems([]);
      setOrderStatus("success");
      refreshActiveOrder();
      setTimeout(() => {
        closeRef.current?.click();
      }, 3000);
    } catch (err: unknown) {
      let msg = t("cart.orderConfirmFailed");
      if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        if (typeof data === "string") msg = data;
        else if (Array.isArray(data) && data[0]?.description) msg = data[0].description;
        else if (data?.message) msg = data.message;
        else if (data?.title) msg = data.title;
        else msg = JSON.stringify(data ?? err.message);
      }
      setOrderError(msg);
      setOrderStatus("error");
    }
  };

  return (
    <div
      className="modal fade"
      id="sepetModal"
      tabIndex={-1}
      aria-labelledby="sepetModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          {/* Desktop header */}
          <div className="modal-header background-red d-none d-sm-flex">
            <h5 className="modal-title text-white" id="sepetModalLabel">
              <i className="bi bi-cart-fill me-2"></i>{t("cart.title")}
            </h5>
            <button
              ref={closeRef}
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          {/* Mobile header with stepper */}
          <div className="d-block d-sm-none">
            <div className="d-flex align-items-center px-3 pt-3 pb-2">
              <button
                ref={closeRef}
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t("common.close")}
              />
              <div className="ms-3">
                <div className="fw-bold">{t("cart.title")}</div>
              </div>
            </div>
            <div className="mobile-stepper">
              <div className={`mobile-stepper__step${orderStatus === "success" ? " mobile-stepper__step--done" : ""}`}>
                <div className={`mobile-stepper__circle mobile-stepper__circle--done`}>1</div>
                <span className="mobile-stepper__label">{t("cart.stepMenu")}</span>
              </div>
              <div className="mobile-stepper__line mobile-stepper__line--done"></div>
              <div className={`mobile-stepper__step${orderStatus !== "confirming" && orderStatus !== "placing" && orderStatus !== "success" ? " mobile-stepper__step--active" : (orderStatus === "success" ? " mobile-stepper__step--done" : "")}`}>
                <div className={`mobile-stepper__circle${orderStatus !== "confirming" && orderStatus !== "placing" && orderStatus !== "success" ? " mobile-stepper__circle--active" : " mobile-stepper__circle--done"}`}>2</div>
                <span className="mobile-stepper__label">{t("cart.stepCart")}</span>
              </div>
              <div className={`mobile-stepper__line${orderStatus === "confirming" || orderStatus === "placing" || orderStatus === "success" ? " mobile-stepper__line--done" : ""}`}></div>
              <div className={`mobile-stepper__step${orderStatus === "confirming" || orderStatus === "placing" ? " mobile-stepper__step--active" : (orderStatus === "success" ? " mobile-stepper__step--done" : "")}`}>
                <div className={`mobile-stepper__circle${orderStatus === "confirming" || orderStatus === "placing" ? " mobile-stepper__circle--active" : (orderStatus === "success" ? " mobile-stepper__circle--done" : "")}`}>3</div>
                <span className="mobile-stepper__label">{t("cart.stepPayment")}</span>
              </div>
            </div>
          </div>

          <div className="modal-body p-0">
            {orderStatus === "success" ? (
              <div className="text-center py-5">
                <i
                  className="bi bi-check-circle-fill text-success"
                  style={{ fontSize: "3.5rem" }}
                ></i>
                <p className="mt-3 fw-bold fs-5">{t("cart.orderSuccess")}</p>
                <p className="text-muted small">{t("common.windowClosing")}</p>
              </div>
            ) : (
              <>
                {/* Adres satırı */}
                <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
                  {defaultAddr ? (
                    <>
                      <i
                        className={`bi bi-geo-alt-fill ${
                          addressValid ? "text-success" : "text-danger"
                        }`}
                      ></i>
                      <div className="flex-grow-1 small">
                        <span className="fw-semibold">
                          {defaultAddr.city.cityNameTr} / {defaultAddr.town.townNameTr}
                        </span>
                        {defaultAddr.customerAddressDescription && (
                          <span className="text-muted ms-1">
                            — {defaultAddr.customerAddressDescription}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-geo-alt text-muted"></i>
                      <span className="flex-grow-1 small text-muted">
                        {t("cart.noAddress")}
                      </span>
                    </>
                  )}
                  <button
                    className="btn btn-sm btn-outline-secondary py-0"
                    style={{ fontSize: "0.75rem" }}
                    data-bs-dismiss="modal"
                    disabled={orderStatus === "confirming" || orderStatus === "placing"}
                    onClick={() => { changeAddressClickedRef.current = true; }}
                  >
                    {t("cart.change")}
                  </button>
                </div>

                {/* Ürün listesi */}
                {items.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-cart-x sepet-empty-icon"></i>
                    <p className="mt-2">{t("cart.empty")}</p>
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {items.map((item, index) => (
                      <li key={index} className="list-group-item px-3 py-3">
                        <div className="d-flex gap-3 align-items-start">
                          {item.foodImagePath && (
                            <img
                              src={S3_IMAGE_BASE_URL + item.foodImagePath}
                              alt={item.foodName}
                              className="sepet-item-img rounded-3"
                            />
                          )}
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-semibold">{item.foodName}</span>
                              <div className="d-flex align-items-center gap-2">
                                {item.discountedPrice != null && item.discountedPrice < item.foodPrice ? (
                                  <span className="badge bg-danger rounded-pill d-flex align-items-center gap-1">
                                    <s className="opacity-75" style={{ fontSize: "0.7rem" }}>{item.foodPrice}₺</s>
                                    {item.foodQuantity}x {item.discountedPrice}₺
                                    <i className="bi bi-percent"></i>
                                  </span>
                                ) : (
                                  <span className="badge bg-danger rounded-pill">
                                    {item.foodQuantity}x {item.foodPrice}₺
                                  </span>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-danger rounded-circle sepet-remove-btn"
                                  onClick={() => removeItem(index)}
                                  disabled={orderStatus === "confirming" || orderStatus === "placing"}
                                  aria-label="Sil"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            </div>
                            {item.menuUpgrade.length > 0 && (
                              <div className="mt-1">
                                {item.menuUpgrade.map((m, mi) => (
                                  <small key={mi} className="text-muted d-block">
                                    <i className="bi bi-plus-circle me-1"></i>
                                    {m.menuUpgradeTitle}
                                    {m.menuUpgradePrice > 0 ? (
                                      <span className="text-danger ms-1">
                                        {m.menuUpgradeQuantity > 1
                                          ? `${m.menuUpgradeQuantity} x ${m.menuUpgradePrice}₺ = +${(m.menuUpgradePrice * m.menuUpgradeQuantity).toFixed(2)}₺`
                                          : `+${m.menuUpgradePrice}₺`}
                                      </span>
                                    ) : m.menuUpgradeQuantity > 1 ? (
                                      <span className="ms-1">x{m.menuUpgradeQuantity}</span>
                                    ) : null}
                                  </small>
                                ))}
                              </div>
                            )}
                            {item.foodNote && (
                              <small className="text-muted d-block mt-1">
                                <i className="bi bi-chat-left-text me-1"></i>
                                {item.foodNote}
                              </small>
                            )}
                            <small className="d-block mt-1 fw-semibold text-end">
                              {item.foodQuantity} x (
                              {(
                                unitPriceOf(item) +
                                item.menuUpgrade.reduce(
                                  (s, m) => s + m.menuUpgradePrice * m.menuUpgradeQuantity,
                                  0
                                )
                              ).toFixed(2)}
                              ₺) ={" "}
                              <span className="text-danger">
                                {(
                                  (unitPriceOf(item) +
                                    item.menuUpgrade.reduce(
                                      (s, m) => s + m.menuUpgradePrice * m.menuUpgradeQuantity,
                                      0
                                    )) *
                                  item.foodQuantity
                                ).toFixed(2)}
                                ₺
                              </span>
                            </small>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {orderStatus !== "success" && items.length > 0 && orderStatus !== "confirming" && orderStatus !== "placing" && (
            <div className="px-3 py-2 border-top">
              <small className="fw-semibold text-muted d-block mb-1">
                <i className="bi bi-tag me-1"></i>{t("cart.promoCode")}
              </small>
              <input
                type="text"
                className="form-control form-control-sm rounded-pill"
                placeholder={t("cart.promoCodePlaceholder")}
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </div>
          )}

          {orderStatus !== "success" && items.length > 0 && (
            <div className="modal-footer flex-column align-items-stretch gap-2">
              {orderStatus === "error" && (
                <div className="alert alert-danger py-2 small mb-0 text-center">
                  {orderError || t("cart.orderFailed")}
                </div>
              )}
              {defaultAddr && !addressValid && (
                <small className="text-danger text-center">
                  <i className="bi bi-x-circle me-1"></i>{t("cart.noDelivery")}
                </small>
              )}
              {remaining > 0 && (
                <small className="text-muted text-center">
                  {t("cart.minOrderWarning", { minAmount, remaining: remaining.toFixed(2) })}
                </small>
              )}
              {!(orderStatus === "confirming" || orderStatus === "placing") && localItemDiscount > 0 && (
                <div className="d-flex justify-content-between small">
                  <span className="text-muted">
                    <i className="bi bi-percent me-1"></i>{t("cart.itemDiscount")}
                  </span>
                  <span className="text-success fw-semibold">-{localItemDiscount.toFixed(2)}₺</span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center">
                {!(orderStatus === "confirming" || orderStatus === "placing") && (
                  <span className="fw-bold">{t("cart.total")} {total.toFixed(2)}₺</span>
                )}
                {orderStatus === "confirming" || orderStatus === "placing" ? (
                  <div className="d-flex flex-column gap-2 w-100">
                    {paymentMethods.length > 0 && (
                      <div>
                        <small className="fw-semibold text-muted d-block mb-1">
                          <i className="bi bi-credit-card me-1"></i>{t("cart.paymentMethod")}
                        </small>
                        <select
                          className="form-select form-select-sm rounded-3"
                          value={selectedPaymentId ?? ""}
                          onChange={(e) => setSelectedPaymentId(e.target.value || null)}
                          disabled={orderStatus === "placing"}
                        >
                          <option value="">{t("cart.selectPayment")}</option>
                          {paymentMethods.map((m) => (
                            <option key={m.id} value={m.id}>
                              {lang === "en" ? m.paymentMethodNameEn : m.paymentMethodNameTr}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {((discountAmount != null && discountAmount > 0) ||
                      (itemDiscountTotal != null && itemDiscountTotal > 0)) && (
                      <div className="rounded-3 p-2 mb-1" style={{ background: "#f0fff4", border: "1px solid #b7ebc6" }}>
                        {promoCode.trim() && discountAmount != null && discountAmount > 0 && (
                          <div className="d-flex align-items-center gap-2 mb-2 pb-2" style={{ borderBottom: "1px solid #b7ebc6" }}>
                            <i className="bi bi-tag-fill text-success small"></i>
                            <span className="small text-success fw-semibold">{promoCode.trim()}</span>
                            <span className="small text-muted ms-1">{t("cart.promoApplied")}</span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between small text-muted">
                          <span>{t("cart.originalPrice")}</span>
                          <span className="text-decoration-line-through">{originalTotalPrice?.toFixed(2)}₺</span>
                        </div>
                        {itemDiscountTotal != null && itemDiscountTotal > 0 && (
                          <div className="d-flex justify-content-between small fw-semibold" style={{ color: "#c77700" }}>
                            <span><i className="bi bi-percent me-1"></i>{t("cart.itemDiscount")}</span>
                            <span>-{itemDiscountTotal.toFixed(2)}₺</span>
                          </div>
                        )}
                        {discountAmount != null && discountAmount > 0 && (
                          <div className="d-flex justify-content-between small text-success fw-semibold">
                            <span>{t("cart.discount")}</span>
                            <span>-{discountAmount.toFixed(2)}₺</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">
                        {t("cart.total")} {(preOrderTotalPrice ?? total).toFixed(2)}₺
                      </span>
                      <button
                        type="button"
                        className="btn btn-success rounded-pill px-4"
                        disabled={orderStatus === "placing" || !selectedPaymentId}
                        onClick={handleConfirmOrder}
                      >
                        {orderStatus === "placing" ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            {t("cart.confirming")}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-lg me-1"></i>{t("cart.confirm")}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger rounded-pill px-4"
                    disabled={!orderEnabled || orderStatus === "ordering"}
                    onClick={handlePlaceOrder}
                  >
                    {orderStatus === "ordering" ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Hazırlanıyor...
                      </>
                    ) : (
                      "Sipariş Ver"
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SepetPopup;
